/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { StyleSheet } from 'react-native-unistyles';
import { View, TextStyle, useWindowDimensions } from 'react-native';
import HeaderView, {
  HeaderStyle,
} from '@airasia-phoenix/react-native/widgets/HeaderView';
import {
  getColor,
  getTextStyle,
  pd,
} from '@airasia-phoenix/react-native/utils/utils';
import CurrencyRow from '../../widgets/CurrenciesView/CurrencyRow';
import CurrenciesView, {
  CurrencyItem,
} from '../../widgets/CurrenciesView/CurrenciesView';
import Check from '@move-travel/react-native-phoenix-icons/Check';

export interface CurrencyPageProps {
  onClose: () => void;
  title?: string;
  currencyList: CurrencyItem[];
  selectedCurrencyValue?: CurrencyItem;
  onSelect: (item: CurrencyItem) => void;
  testID?: string;
  showHeader?: boolean;
}

const CurrencyPage = ({
  onClose,
  title = 'Currency',
  currencyList,
  selectedCurrencyValue,
  onSelect,
  showHeader = true,
}: CurrencyPageProps) => {
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<string | null>(
    selectedCurrencyValue?.id || null,
  );
  const [visible, setVisible] = useState(true);
  const [selectedCurrency, setSelectedCurrency] = useState(
    selectedCurrencyValue,
  );
  const [countdown, setCountdown] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { width: screenWidth } = useWindowDimensions();

  const isMobileWidth = screenWidth >= 360 && screenWidth <= 575;

  useEffect(() => {
    if (selectedCurrencyValue) {
      setSelectedCurrency(selectedCurrencyValue);
    }
  }, [selectedCurrencyValue]);

  useEffect(() => {
    if (countdown === null) return;

    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          timerRef.current = null;
          setCountdown(null);
          setVisible(false);
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [countdown]);

  const handleSelect = useCallback(
    (item: CurrencyItem) => {
      console.log('Selected currency:', item);
      setSelectedCurrency(item);
      setSelectedCurrencyId(item.id);
      setVisible(true);
      setCountdown(10);
      onSelect(item);
    },
    [onSelect],
  );

  const greenCheckIcon = useMemo(
    () => ({
      height: 24,
      width: 24,
      children: <Check />,
      tint: pd.ref.palette.green70,
    }),
    [],
  );

  const selectedTextStyle = getTextStyle(
    pd.sys.typescale.body_text_medium,
    pd.sys.colour.content_primary,
  ) as TextStyle;

  const baseTextStyle = getTextStyle(
    pd.sys.typescale.body_text,
    pd.sys.colour.content_primary,
  ) as TextStyle;

  const processedCurrencyList = useMemo(() => {
    return currencyList.map((item) => ({
      ...item,
      endElement:
        item.id === selectedCurrencyId
          ? { iconViewProps: greenCheckIcon }
          : undefined,
      primaryTextStyle: {
        ...(item.id === selectedCurrencyId
          ? {
              ...selectedTextStyle,
              color: getColor(pd.sys.colour.secondary),
            }
          : {
              ...baseTextStyle,
              color: getColor(pd.sys.colour.content_primary),
            }),
      } as TextStyle,
    }));
  }, [
    currencyList,
    selectedCurrencyId,
    greenCheckIcon,
    baseTextStyle,
    selectedTextStyle,
  ]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const chunkArray = useCallback((arr: any[], size: number) => {
    return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
      arr.slice(i * size, i * size + size),
    );
  }, []);

  let numColumns = 3;
  if (screenWidth >= 576 && screenWidth <= 766) numColumns = 2;
  else if (isMobileWidth) numColumns = 1;

  if (isMobileWidth) {
    return (
      <View style={styles.mobileWrapper}>
        {visible && (
          <CurrenciesView
            onClose={() => {
              setVisible(false);
              setCountdown(null);
              if (timerRef.current) clearInterval(timerRef.current);
            }}
            currencyList={currencyList}
            selectedValue={selectedCurrency}
            currencyTitle={title}
            onSelect={handleSelect}
          />
        )}
      </View>
    );
  }
  return (
    <View
      style={[
        { backgroundColor: getColor(pd.sys.colour.container_none) },
        styles.modalContainer,
      ]}
    >
      {showHeader && (
        <HeaderView
          headerStyle={HeaderStyle.IMMERSE}
          startIcon={{
            icon: { icon: pd.icon.main.navigation.close },
            onPress: onClose,
          }}
          titleStart={title}
        />
      )}

      <View>
        {chunkArray(processedCurrencyList, numColumns).map((row, rowIndex) => (
          <View key={rowIndex} style={styles.row}>
            {row.map((item) => (
              <View key={item.id} style={styles.column}>
                <CurrencyRow
                  primaryText={item.primaryText}
                  primaryTextStyle={item.primaryTextStyle}
                  isSelected={item.id === selectedCurrencyId}
                  onPress={() => handleSelect(item)}
                />
              </View>
            ))}
            {Array.from({ length: numColumns - row.length }).map((_, i) => (
              <View
                key={`placeholder-${i}`}
                style={styles.column}
                testID="currency-grid-placeholder"
              />
            ))}
          </View>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  mobileWrapper: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },

  modalContainer: {
    overflow: 'hidden',
    elevation: 2,
    width: '100%',
    // maxWidth: '100%',
    maxHeight: 678,
    paddingBottom: pd.ref.space.spacing80.px,
  },
  heading: {
    marginBottom: pd.ref.space.spacing20.px,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: pd.ref.space.spacing20.px,
    flexWrap: 'wrap',
    gap: pd.ref.space.spacing40.px,
  },
  column: {
    flex: 1,
  },
  title: {
    paddingLeft: pd.ref.space.spacing50.px,
  },
});

export default CurrencyPage;
